import React, { useEffect, useState } from "react";
import { setPageMeta } from "../Util";
import Carousel from "react-bootstrap/Carousel";
import Badge from "react-bootstrap/Badge";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import SectionHeading from "../SectionHeading";
import { BsArrowLeftCircleFill } from "react-icons/bs";
import Spinner from "react-bootstrap/Spinner";
import Link from "@mui/material/Link";
import VehiclePrice from "../fragments/VehiclePrice";
import { breadcrumbNameMap, LISTING_ORDER_PAGE_TYPE } from "../Constants";
import VehicleTitle from "../fragments/VehicleTitle";
import VehicleAttributes from "../fragments/VehicleAttributes";
import {getVehicleImageLinks, VEHICLE_PHOTO_PLACEHOLDER, VEHICLE_PHOTO_PLACEHOLDER_ALT} from "../vehicleImages";

export default function VehicleView({ articleId, currencyExchangeRate, pageType }) {
    let [searchParams] = useSearchParams();
    let navigate = useNavigate();
    let location = useLocation();

    const [article, setArticle] = useState(null);
    const [articleLoading, setArticleLoading] = useState(true);
    const imageLinks = getVehicleImageLinks(article?.imageLinks);
    const articleImageAlt = article
        ? [article.brand, article.model, article.configuration, article.year].filter(Boolean).join(" ")
        : "";

    /* Page metadata for listing */
    const listPageMeta = breadcrumbNameMap["/listing"].pageMeta;

    useEffect(() => {
        if (!articleId) {
            setArticle(null);
            setArticleLoading(false);
            return;
        }

        const controller = new AbortController();

        const getArticle = async (id, signal) => {
            setArticleLoading(true);
            try {
                const res = await fetch(`/api/cars/${id}`, { signal });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (!signal.aborted) {
                    setArticle(data);
                }
            } catch (err) {
                if (err.name !== "AbortError") {
                    console.error(err);
                    if (!signal.aborted) {
                        setArticle(null);
                    }
                }
            } finally {
                if (!controller.signal.aborted) {
                    setArticleLoading(false);
                }
            }
        };

        getArticle(articleId, controller.signal);

        return () => controller.abort();
    }, [articleId]);

    useEffect(() => {
        if (article) {
            const parts = [article.brand, article.model];
            if (article.configuration) parts.push(article.configuration);
            parts.push(article.year);
            const articleTitle = parts.join(", ");

            const pageMeta =
                pageType === LISTING_ORDER_PAGE_TYPE
                    ? { ...breadcrumbNameMap["/vehicleOrder/catalog/:articleId"].pageMeta }
                    : { ...breadcrumbNameMap["/listing/:articleId"].pageMeta };

            pageMeta.title = articleTitle + " " + pageMeta.title;
            pageMeta.description = articleTitle + ". " + pageMeta.description;
            pageMeta.keywords = articleTitle + ", " + pageMeta.keywords;

            setPageMeta(pageMeta);
        }

        return function () {
            setPageMeta(listPageMeta);
        };
    }, [article, pageType, listPageMeta]);

    function GoBackLink() {
        return (
            <div className="row mb-4">
                <div className="col text-right">
                    <div className="d-flex justify-content-end">
                        <Link
                            onClick={() =>
                                location.key !== "default"
                                    ? navigate(-1)
                                    : navigate("/listing", { replace: true })
                            }
                        >
                            <BsArrowLeftCircleFill /> к списку авто
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="site-section">
            <div className="container">
                {articleLoading ? (
                    <Spinner animation="border" size="sm" className="me-1" role="status">
                        <span className="visually-hidden">Загружаем информацию...</span>
                    </Spinner>
                ) : !article ? (
                    <span>Нет информации для просмотра</span>
                ) : (
                    <>
                        <GoBackLink />
                        <SectionHeading sectionHeadingTitle={<VehicleTitle vehicle={article} />} />
                        {article.vin && (
                            <small className="fw-lighter fst-italic">VIN {article.vin}</small>
                        )}

                        {/* Price */}
                        <VehiclePrice
                            price={article.price}
                            currencyExchangeRate={currencyExchangeRate}
                            className="fs-5"
                        />

                        <div className="container-fluid py-5">
                            <div className="row">
                                {/* Photo & Stock*/}
                                <div className="col-lg-6 position-relative">
                                    {imageLinks.length ? (
                                        <Carousel slide={false}>
                                            {imageLinks.map((imageLink, index) => (
                                                <Carousel.Item key={imageLink || index}>
                                                    <img
                                                        className="d-block w-100"
                                                        src={imageLink}
                                                        alt={`${articleImageAlt} фото ${index + 1}`}
                                                    />
                                                </Carousel.Item>
                                            ))}
                                        </Carousel>
                                    ) : (
                                        <img
                                            className="d-block w-100"
                                            src={VEHICLE_PHOTO_PLACEHOLDER}
                                            alt={VEHICLE_PHOTO_PLACEHOLDER_ALT}
                                        />
                                    )}
                                    <div className="position-absolute top-0 end-0 fs-4 my-1 mx-3">
                                        <Badge
                                            bg={article.stockStatus === "IN_STOCK" ? "success" : "secondary"}
                                        >
                                            {article.stockStatus === "IN_STOCK" ? "В наличии" : "Под заказ"}
                                        </Badge>
                                    </div>
                                </div>

                                <div className="col-lg-6">
                                    {/* Text Description */}
                                    <p
                                        style={{ whiteSpace: "pre-line" }}
                                        className="mb-4 lh-lg shadow-lg p-3 mb-5 rounded"
                                    >
                                        {article.textDescription}
                                    </p>

                                    {/* Tabs */}
                                    <div className="row mt-5">
                                        <div className="col">
                                            <div className="nav nav-tabs justify-content-center border-secondary mb-4">
                                                <button
                                                    className="nav-link active"
                                                    id="tab-1"
                                                    data-bs-toggle="tab"
                                                    data-bs-target="#tab-pane-1"
                                                    type="button"
                                                    role="tab"
                                                    aria-controls="tab-1-pane"
                                                    aria-selected="true"
                                                >
                                                    Основные Характеристики
                                                </button>
                                                <button
                                                    className="nav-link"
                                                    id="tab-3"
                                                    data-bs-toggle="tab"
                                                    data-bs-target="#tab-pane-3"
                                                    type="button"
                                                    role="tab"
                                                    aria-controls="tab-3-pane"
                                                    aria-selected="false"
                                                >
                                                    Опции
                                                </button>
                                                <button
                                                    className="nav-link"
                                                    id="tab-4"
                                                    data-bs-toggle="tab"
                                                    data-bs-target="#tab-pane-4"
                                                    type="button"
                                                    role="tab"
                                                    aria-controls="tab-4-pane"
                                                    aria-selected="false"
                                                >
                                                    Видеообзор
                                                </button>
                                            </div>
                                            <div className="tab-content">
                                                <div className="tab-pane fade show active" id="tab-pane-1">
                                                    <h4 className="mb-3">Основные Характеристики</h4>
                                                    <div className="d-flex flex-column mb-3 mt-2 border-bottom pb-3">
                                                        <VehicleAttributes attributes={article.vehicleAttributes} />
                                                    </div>
                                                </div>
                                                <div className="tab-pane fade" id="tab-pane-3">
                                                    <h4 className="mb-3">Опции</h4>
                                                    <ul className="list-group list-group-flush">
                                                        {!article.options && <span>Не указано</span>}
                                                        {article.options &&
                                                            article.options.map((option, index) =>
                                                                Object.entries(option).map(
                                                                    ([key, value], subIndex) => (
                                                                        <li
                                                                            key={`${index}-${subIndex}`}
                                                                            className="list-group-item px-0"
                                                                        >
                                                                            ✔️{key}:<br />{" "}
                                                                            {Array.isArray(value)
                                                                                ? value.join(", ") + "."
                                                                                : "-"}
                                                                        </li>
                                                                    )
                                                                )
                                                            )}
                                                    </ul>
                                                </div>
                                                <div className="tab-pane fade" id="tab-pane-4">
                                                    <h4 className="mb-3">Видеообзор</h4>
                                                    {/* Video */}
                                                    {!article.videoLink && (
                                                        <span>Нет информации для просмотра.</span>
                                                    )}
                                                    {article.videoLink && (
                                                        <div className="ratio ratio-16x9 mt-5">
                                                            <iframe
                                                                width="560"
                                                                height="315"
                                                                src={article.videoLink}
                                                                title="Видео плейер"
                                                                frameBorder="0"
                                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                                allowFullScreen
                                                            ></iframe>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <GoBackLink />
                    </>
                )}
            </div>
        </div>
    );
}
